import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { HubCustomField, HubCustomSection } from "@/types/agent/info-links";
import type { GeniePageSectionIdea } from "@/types/genie-documents/genie-document";
import { RecommendFromDocumentsButton } from "./RecommendFromDocumentsButton";

/** Mirrors the server's sanitiser caps — what can't be served isn't offered. */
const MAX_SECTIONS = 6;
const MAX_FIELDS = 12;
const MAX_TITLE = 80;
const MAX_INTRO = 400;
const MAX_LABEL = 60;
const MAX_VALUE = 300;

const inputClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary";

interface CustomSectionsEditorProps {
  value: HubCustomSection[];
  onChange: (sections: HubCustomSection[]) => void;
  /**
   * Sections drafted from the genie's documents, ready to add. Null means the
   * ideas haven't been fetched yet — the block prompts for a recommendation.
   * Omit (with `onRecommend`) to hide the suggestions block entirely.
   */
  ideas?: GeniePageSectionIdea[] | null;
  /** Ask the genie to draft section ideas from its documents. */
  onRecommend?: () => void;
  isRecommending?: boolean;
  /** False when the genie has no knowledge sources to read. */
  hasSources?: boolean;
}

/**
 * Edit the owner-written sections of a Genie Page. Structured on purpose —
 * a title, an optional lead-in and labelled fields — so whatever an owner
 * types renders like a band we designed, never like a pasted document.
 */
export const CustomSectionsEditor = ({
  value,
  onChange,
  ideas = null,
  onRecommend,
  isRecommending = false,
  hasSources = false,
}: CustomSectionsEditorProps) => {
  const patchSection = (id: string, patch: Partial<HubCustomSection>) =>
    onChange(value.map((section) => (section.id === id ? { ...section, ...patch } : section)));

  const patchField = (sectionId: string, fieldId: string, patch: Partial<HubCustomField>) =>
    onChange(
      value.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            fields: section.fields.map((field) =>
              field.id === fieldId ? { ...field, ...patch } : field,
            ),
          }
          : section,
      ),
    );

  const addSection = () =>
    onChange([
      ...value,
      {
        id: crypto.randomUUID(),
        title: "",
        intro: "",
        fields: [{ id: crypto.randomUUID(), label: "", value: "" }],
      },
    ]);

  const addField = (sectionId: string) =>
    onChange(
      value.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            fields: [...section.fields, { id: crypto.randomUUID(), label: "", value: "" }],
          }
          : section,
      ),
    );

  const removeField = (sectionId: string, fieldId: string) =>
    onChange(
      value.map((section) =>
        section.id === sectionId
          ? { ...section, fields: section.fields.filter((field) => field.id !== fieldId) }
          : section,
      ),
    );

  const atCapacity = value.length >= MAX_SECTIONS;

  // Ideas already on the page (by title) aren't offered again.
  const freshIdeas = (ideas ?? []).filter(
    (idea) =>
      !value.some(
        (section) => section.title.trim().toLowerCase() === idea.title.trim().toLowerCase(),
      ),
  );

  const addIdea = (idea: GeniePageSectionIdea) => {
    if (atCapacity) return;
    onChange([
      ...value,
      {
        id: crypto.randomUUID(),
        title: idea.title,
        intro: idea.intro ?? "",
        fields:
          idea.fields.length > 0
            ? idea.fields.map((field) => ({
                id: crypto.randomUUID(),
                label: field.label,
                value: field.value,
              }))
            : [{ id: crypto.randomUUID(), label: "", value: "" }],
      },
    ]);
  };

  return (
    <div className="space-y-4">
      {value.map((section) => (
        <div key={section.id} className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Section title
                </label>
                <input
                  value={section.title}
                  onChange={(event) =>
                    patchSection(section.id, { title: event.target.value.slice(0, MAX_TITLE) })
                  }
                  placeholder="Visit us"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Lead-in <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={section.intro ?? ""}
                  onChange={(event) =>
                    patchSection(section.id, { intro: event.target.value.slice(0, MAX_INTRO) })
                  }
                  rows={2}
                  placeholder="Prefer to come in? Here's where to find us."
                  className={`${inputClass} resize-y`}
                />
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-gray-400 hover:text-red-600"
              onClick={() => onChange(value.filter((entry) => entry.id !== section.id))}
              aria-label="Remove this section"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-2">
            {section.fields.map((field) => (
              <div key={field.id} className="flex items-center gap-2">
                <input
                  value={field.label}
                  onChange={(event) =>
                    patchField(section.id, field.id, {
                      label: event.target.value.slice(0, MAX_LABEL),
                    })
                  }
                  placeholder="Street address"
                  aria-label="Field label"
                  className={`${inputClass} max-w-[40%]`}
                />
                <input
                  value={field.value}
                  onChange={(event) =>
                    patchField(section.id, field.id, {
                      value: event.target.value.slice(0, MAX_VALUE),
                    })
                  }
                  placeholder="12 Queen Street, Auckland"
                  aria-label="Field value"
                  className={inputClass}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-gray-400 hover:text-red-600"
                  onClick={() => removeField(section.id, field.id)}
                  aria-label="Remove this field"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {section.fields.length < MAX_FIELDS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => addField(section.id)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add a field
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Web addresses, emails and phone numbers become tappable links on the page.
          </p>
        </div>
      ))}

      {!atCapacity && (
        <Button type="button" variant="outline" className="gap-1.5" onClick={addSection}>
          <Plus className="h-4 w-4" />
          Add a section
        </Button>
      )}

      {onRecommend && (
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-700">Suggestions</span>
            <RecommendFromDocumentsButton
              onClick={onRecommend}
              isPending={isRecommending}
              hasSources={hasSources}
            />
          </div>
          {ideas === null ? (
            <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
              {hasSources
                ? "Tap “Recommend from my documents” and your Genie will draft sections from the details in its sources — policies, prices, booking details."
                : "Add a source to this Genie's knowledge base first — then it can draft sections from what it knows."}
            </p>
          ) : freshIdeas.length > 0 ? (
            <ul className="space-y-2">
              {freshIdeas.map((idea) => (
                <li
                  key={idea.title}
                  className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{idea.title}</p>
                    <p className="text-[12.5px] text-gray-500">
                      {idea.fields.length} {idea.fields.length === 1 ? "field" : "fields"}
                      {idea.intro ? " · with a lead-in" : ""}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={atCapacity}
                    onClick={() => addIdea(idea)}
                    className="shrink-0 gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
              No new sections in this draft — anything it found is already on the page.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSectionsEditor;
